import React, { useRef, useState } from 'react';
import { PortalApiError, portalApi } from '../api';
import { getAccessToken } from '../authStorage';
import { DISPLAY_NAME_MAX_LENGTH, SIGNATURE_MAX_LENGTH } from '../constants';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
import { compressImageFile, userMediaUrl } from '../profileMedia';
/**
 * 手机 Web「我的资料」：改名称/签名，展开并替换头像，设置背景图。
 */
export default function ProfileEditPage({ onBack, onUserUpdated, user }) {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [nameDraft, setNameDraft] = useState(user.displayName || '');
    const [signatureDraft, setSignatureDraft] = useState(user.signature || '');
    const [view, setView] = useState('main');
    const avatarInputRef = useRef(null);
    const backgroundInputRef = useRef(null);
    const displayName = user.displayName || user.phone || '用户';
    const avatarText = displayName.slice(0, 1).toUpperCase();
    const avatarSrc = userMediaUrl('avatar', user);
    const backgroundSrc = userMediaUrl('background', user);
    const signatureText = user.signature?.trim() || M.PROFILE_SIGNATURE_PLACEHOLDER;
    const requireToken = () => {
        const token = getAccessToken();
        if (!token) {
            setError(M.NEED_LOGIN_FIRST);
            logger.warn(`[我的资料] ${new Date().toISOString()} 缺少 accessToken`);
        }
        return token;
    };
    const applyUser = (next) => {
        onUserUpdated(next);
        setError('');
        logger.info(`[我的资料] ${new Date().toISOString()} 资料已同步`
            + ` | userId=${next.id} hasAvatar=${Boolean(next.hasAvatar)} hasBackground=${Boolean(next.hasBackground)}`);
    };
    const onHeaderBack = () => {
        if (view === 'main') {
            onBack();
            return;
        }
        setView('main');
        setError('');
        setNameDraft(user.displayName || '');
        setSignatureDraft(user.signature || '');
    };
    const onSaveName = async (event) => {
        event.preventDefault();
        const nextName = nameDraft.trim();
        if (!nextName) {
            setError(M.PROFILE_NAME_REQUIRED);
            return;
        }
        const token = requireToken();
        if (!token) {
            return;
        }
        setLoading(true);
        setError('');
        try {
            const next = await portalApi.updateProfile(token, {
                displayName: nextName,
                signature: user.signature || ''
            });
            applyUser(next);
            setView('main');
            logger.info(`[我的资料] ${new Date().toISOString()} 已保存名称 | len=${nextName.length}`);
        }
        catch (e) {
            const message = e instanceof PortalApiError ? e.message : (e?.message || M.BUSY);
            setError(message);
            logger.warn(`[我的资料] ${new Date().toISOString()} 保存名称失败 | ${message}`);
        }
        finally {
            setLoading(false);
        }
    };
    const onSaveSignature = async (event) => {
        event.preventDefault();
        const token = requireToken();
        if (!token) {
            return;
        }
        setLoading(true);
        setError('');
        try {
            const next = await portalApi.updateProfile(token, {
                displayName: user.displayName,
                signature: signatureDraft.trim()
            });
            applyUser(next);
            setView('main');
            logger.info(`[我的资料] ${new Date().toISOString()} 已保存签名 | len=${signatureDraft.trim().length}`);
        }
        catch (e) {
            const message = e instanceof PortalApiError ? e.message : (e?.message || M.BUSY);
            setError(message);
            logger.warn(`[我的资料] ${new Date().toISOString()} 保存签名失败 | ${message}`);
        }
        finally {
            setLoading(false);
        }
    };
    const onPickFile = async (kind, fileList) => {
        const file = fileList?.[0];
        if (!file) {
            return;
        }
        if (!file.type.startsWith('image/')) {
            setError(M.PROFILE_IMAGE_TYPE);
            logger.warn(`[我的资料] ${new Date().toISOString()} 非图片文件 | type=${file.type}`);
            return;
        }
        const token = requireToken();
        if (!token) {
            return;
        }
        setLoading(true);
        setError('');
        try {
            const blob = await compressImageFile(file, kind);
            const next = kind === 'avatar'
                ? await portalApi.uploadAvatar(token, blob)
                : await portalApi.uploadBackground(token, blob);
            applyUser(next);
            if (kind === 'avatar') {
                setView('main');
            }
            logger.info(`[我的资料] ${new Date().toISOString()} 已上传${kind === 'avatar' ? '头像' : '背景图'}`);
        }
        catch (e) {
            const message = e instanceof PortalApiError ? e.message : (e?.message || M.PROFILE_UPLOAD_FAILED);
            setError(message);
            logger.warn(`[我的资料] ${new Date().toISOString()} 上传${kind}失败 | ${message}`);
        }
        finally {
            setLoading(false);
            if (avatarInputRef.current) {
                avatarInputRef.current.value = '';
            }
            if (backgroundInputRef.current) {
                backgroundInputRef.current.value = '';
            }
        }
    };
    const title = view === 'name'
        ? M.PROFILE_FIELD_NAME
        : view === 'signature'
            ? M.PROFILE_FIELD_SIGNATURE
            : view === 'avatarPreview'
                ? M.PROFILE_MY_INFO
                : M.PROFILE_MY_INFO;
    if (view === 'name' || view === 'signature') {
        const isName = view === 'name';
        return (React.createElement("div", { className: 'portal-profile portal-profile-detail-view' },
            React.createElement("header", { className: 'portal-profile-detail-header' },
                React.createElement("button", { "aria-label": M.HISTORY_BACK, className: 'portal-profile-detail-back', onClick: onHeaderBack, type: 'button' }, '‹'),
                React.createElement("span", { className: 'portal-profile-detail-title' }, title)),
            React.createElement("div", { className: 'portal-profile-detail-body' },
                React.createElement("form", { className: 'portal-security-form', onSubmit: isName ? onSaveName : onSaveSignature },
                    error ? React.createElement("div", { className: 'portal-error inline' }, error) : null,
                    React.createElement("p", { className: 'portal-security-hint' }, isName ? M.PROFILE_NAME_HINT : M.PROFILE_SIGNATURE_HINT),
                    isName ? (React.createElement("input", { autoComplete: 'nickname', maxLength: DISPLAY_NAME_MAX_LENGTH, onChange: event => setNameDraft(event.target.value), type: 'text', value: nameDraft })) : (React.createElement("textarea", { maxLength: SIGNATURE_MAX_LENGTH, onChange: event => setSignatureDraft(event.target.value), rows: 4, value: signatureDraft })),
                    React.createElement("button", { className: 'portal-primary-btn', disabled: loading, type: 'submit' }, loading ? M.BUSY : M.PROFILE_SAVE)))));
    }
    if (view === 'avatarPreview') {
        return (React.createElement("div", { className: 'portal-profile-avatar-preview' },
            React.createElement("button", { "aria-label": M.HISTORY_BACK, className: 'portal-profile-detail-back portal-profile-avatar-preview-back', onClick: onHeaderBack, type: 'button' }, '‹'),
            React.createElement("div", { className: 'portal-profile-avatar-preview-stage' }, avatarSrc ? (React.createElement("img", { alt: displayName, onError: event => {
                    event.currentTarget.style.display = 'none';
                }, src: avatarSrc })) : (React.createElement("span", { className: 'portal-profile-avatar-preview-fallback' }, avatarText))),
            React.createElement("button", { className: 'portal-profile-quota-upgrade', disabled: loading, onClick: () => avatarInputRef.current?.click(), type: 'button' }, loading ? M.BUSY : M.PROFILE_REPLACE_AVATAR),
            React.createElement("input", { accept: 'image/*', hidden: true, onChange: event => onPickFile('avatar', event.target.files), ref: avatarInputRef, type: 'file' }),
            error ? React.createElement("div", { className: 'portal-error inline' }, error) : null));
    }
    return (React.createElement("div", { className: 'portal-profile portal-profile-edit' },
        React.createElement("section", { "aria-label": M.PROFILE_SET_BACKGROUND, className: 'portal-profile-edit-hero' },
            backgroundSrc ? (React.createElement("img", { alt: '', className: 'portal-profile-edit-hero-img', onError: event => {
                    event.currentTarget.style.display = 'none';
                }, src: backgroundSrc })) : null,
            React.createElement("button", { "aria-label": M.HISTORY_BACK, className: 'portal-profile-detail-back portal-profile-edit-back', onClick: onHeaderBack, type: 'button' }, '‹'),
            React.createElement("button", { "aria-label": M.PROFILE_SET_BACKGROUND, className: 'portal-profile-edit-hero-btn', disabled: loading, onClick: () => backgroundInputRef.current?.click(), type: 'button' }, backgroundSrc ? '' : M.PROFILE_SET_BACKGROUND),
            React.createElement("button", { "aria-label": M.PROFILE_REPLACE_AVATAR, className: 'portal-profile-edit-avatar', onClick: () => setView('avatarPreview'), type: 'button' }, avatarSrc ? (React.createElement("img", { alt: displayName, onError: event => {
                    event.currentTarget.style.display = 'none';
                }, src: avatarSrc })) : (React.createElement("span", null, avatarText)))),
        React.createElement("section", { className: 'portal-security-list' },
            React.createElement("button", { className: 'portal-security-row portal-security-row-btn', onClick: () => {
                    setNameDraft(user.displayName || '');
                    setView('name');
                }, type: 'button' },
                React.createElement("span", { className: 'portal-security-label' }, M.PROFILE_FIELD_NAME),
                React.createElement("span", { className: 'portal-security-value' },
                    displayName,
                    ' ›')),
            React.createElement("button", { className: 'portal-security-row portal-security-row-btn', onClick: () => {
                    setSignatureDraft(user.signature || '');
                    setView('signature');
                }, type: 'button' },
                React.createElement("span", { className: 'portal-security-label' }, M.PROFILE_FIELD_SIGNATURE),
                React.createElement("span", { className: 'portal-security-value' },
                    signatureText,
                    ' ›'))),
        error ? React.createElement("div", { className: 'portal-error inline' }, error) : null,
        React.createElement("input", { accept: 'image/*', hidden: true, onChange: event => onPickFile('background', event.target.files), ref: backgroundInputRef, type: 'file' })));
}
